import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import ScreenLoading from 'components/ScreenLoading'
import { selectToken } from 'containers/authentication/authenticationSlice'
import { storeShopifyParams, buildShopifyQueryString, getStoredShopifyParams, clearShopifyParams, shouldStoreShopifyParams, markShopifyResumePending } from 'utils/shopifyParams'
import useGetOrganizations from 'containers/organizations/useGetOrganizations'
import useStorage from 'containers/useStorage'
import api from 'containers/api'
import { SHOPIFY_APP_STORE_URL } from 'constants/shopify'
import { announceServiceAccountConnected } from 'utils/serviceAccountEvents'

interface Organization {
  id: number
  slug: string
  name: string
  is_demo?: boolean
}

const DEMO_ORG_SLUG = 'demo'

// A user-facing error: a clear title + explanation, and whether to offer "Contact
// support" (for cases the user can't self-resolve, e.g. the store is linked elsewhere).
interface IntegrationError {
  title: string
  message: string
  showSupport?: boolean
  // Whether re-running the connect flow could succeed (transient/recoverable). Drives the
  // "Try again" action. The Shopify params are kept in localStorage on error, so a retry
  // can resume from where it failed.
  canRetry?: boolean
  // Whether the fix is "(re)install from the App Store". Set when we have no usable shop
  // context: retrying can't invent one, and the listing is the only entry point that yields
  // a shop domain + OAuth grant. Drives the "Install from the Shopify App Store" action.
  showAppStore?: boolean
}

// Map known backend error types (api/routers/shopify_billing.py) to friendly copy.
// Anything unmapped falls back to the backend's own `detail.message` (which is usually
// specific and helpful) rather than a generic "try again".
const SHOPIFY_ERROR_MAP: Record<string, IntegrationError> = {
  shop_linked_to_other_org: {
    title: 'This store is connected to another account',
    message:
      'This Shopify store is already linked to a different Maya organization. ' +
      'Disconnect it from that organization first, or contact us and we’ll help you move it.',
    showSupport: true,
  },
  shop_required: {
    title: 'We couldn’t identify your store',
    message:
      'We didn’t receive your Shopify store details. Install Maya Insights from the ' +
      'Shopify App Store, pick the store you want to connect, and we’ll take it from there.',
    showAppStore: true,
    showSupport: true,
  },
}

const toIntegrationError = (error: any, fallbackTitle: string): IntegrationError => {
  const detail = error?.response?.data?.detail
  if (detail?.type && SHOPIFY_ERROR_MAP[detail.type]) {
    const mapped = SHOPIFY_ERROR_MAP[detail.type]
    // The backend names the other organization only when the caller is a member of it —
    // use it to tell them exactly where the store lives instead of the generic copy.
    if (detail.type === 'shop_linked_to_other_org' && detail.organization_name) {
      return {
        ...mapped,
        message:
          `This Shopify store is already connected to your Maya organization “${detail.organization_name}”. ` +
          'Disconnect it from that organization first, or contact us and we’ll help you move it.',
      }
    }
    return mapped
  }
  return {
    title: fallbackTitle,
    // Surface the backend's specific message when we have one — it's far more useful
    // than a blanket "please try again".
    message:
      detail?.message ||
      'Something went wrong connecting your Shopify store. Please try again, or contact us if it keeps happening.',
    showSupport: true,
    canRetry: true,
  }
}

const ShopifyIntegration: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const token = useSelector(selectToken)
  const storage = useStorage()
  const [error, setError] = useState<IntegrationError | null>(null)
  const [status, setStatus] = useState<string>('Initializing...')
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [showOrgPicker, setShowOrgPicker] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const {
    getOrganizations,
    organizations,
    loadingOrganizations,
  } = useGetOrganizations()

  // Load organizations on mount
  useEffect(() => {
    if (token) {
      getOrganizations()
    }
  }, [token])

  // Filter out demo organization and get user's real organizations
  const getUserOrganizations = (): Organization[] => {
    let localOrgs: Organization[] = []
    localOrgs = organizations || []
    if (!localOrgs || localOrgs?.length === 0) {
      return []
    }
    // Exclude the demo org by its is_demo flag (the app-wide signal) — not just the
    // 'demo' slug, which varies by environment. If the demo leaks in, it becomes
    // userOrgs[0] and the connect runs against an org the user isn't a member of -> 401.
    // Filtering it out means a first-time user (demo only) correctly falls through to the
    // create-org / sign-in onboarding flow.
    return localOrgs.filter((org: Organization) => !org.is_demo && org.slug !== DEMO_ORG_SLUG)
  }

  // Check if user needs to pick an organization
  useEffect(() => {
    if (!token || loadingOrganizations) return

    const userOrgs = getUserOrganizations()

    // If user has 0 or 1 real organization, auto-select and proceed
    if (userOrgs.length <= 1) {
      if (userOrgs.length === 1) {
        setSelectedOrganization(userOrgs[0])
      }
      // Will be handled by handleShopifyRedirect
      return
    }

    // An explicit ?org=<slug> means the caller already knows which organization this is for
    // — e.g. Details.tsx handing off a Stripe checkout that was rejected *inside* that org.
    // Honour it rather than asking again: a picker here lets the merchant link the shop to a
    // DIFFERENT org than the one they were buying for, and moving a store afterwards is not
    // self-serve (#1679). Matched against the user's OWN orgs — getUserOrganizations already
    // drops the demo org — so an unknown or unauthorized slug falls through to the picker
    // instead of being trusted.
    const requestedSlug = new URLSearchParams(location.search).get('org')
    const requestedOrg = requestedSlug
      ? userOrgs.find((org: Organization) => org.slug === requestedSlug)
      : undefined
    if (requestedOrg) {
      setSelectedOrganization(requestedOrg)
      return
    }

    // User has multiple organizations - show picker
    setShowOrgPicker(true)
    setStatus('Select an organization')
  }, [token, loadingOrganizations, organizations, location.search])

  // Handle the Shopify redirect flow
  useEffect(() => {
    // Only proceed once we know the auth state
    if (!token) {
      handleShopifyRedirect()
      return
    }

    // Wait for organizations to load
    if (loadingOrganizations) {
      setStatus('Loading organizations...')
      return
    }

    const userOrgs = getUserOrganizations()

    // If showing org picker, wait for selection
    if (userOrgs.length > 1 && !selectedOrganization) {
      return
    }

    // Auto-select if only one org
    if (userOrgs.length === 1 && !selectedOrganization) {
      setSelectedOrganization(userOrgs[0])
      return
    }

    // Proceed when an org is selected, OR when there's no real org at all (e.g. only the
    // demo): handleShopifyRedirect then sends them to create one. Without the zero-org
    // case this stalled on "Loading organizations…" — the create-org redirect inside
    // handleShopifyRedirect was never reached.
    if (selectedOrganization || userOrgs.length === 0) {
      handleShopifyRedirect()
    }
  }, [token, loadingOrganizations, organizations, selectedOrganization, location.search])

  const handleOrganizationSelect = (org: Organization) => {
    setSelectedOrganization(org)
    setShowOrgPicker(false)
    setIsProcessing(true)
  }

  const handleShopifyRedirect = async () => {
    // get query params from URL
    const queryParams = new URLSearchParams(location.search)
    const shop = queryParams.get('shop')
    const host = queryParams.get('host')
    const hmac = queryParams.get('hmac')

    // Store params in localStorage for later use (survives auth flow) — but ONLY on a fresh
    // arrival from Shopify. When we're reached via an internal resume redirect (shopify=true),
    // the params already exist and re-storing would refresh their 24h expiry, keeping a
    // stale/abandoned flow looping the app root back to Shopify connect.
    if (shop && shouldStoreShopifyParams(location.search)) {
      storeShopifyParams(shop, host || undefined, hmac || undefined)
    }

    // Get shop from URL or localStorage
    const storedParams = getStoredShopifyParams()
    const shopDomain = shop || storedParams?.shop

    if (!shopDomain) {
      setError({
        title: 'We couldn’t identify your store',
        message:
          'No Shopify store information was found. Install Maya Insights from the Shopify ' +
          'App Store and pick the store you want to connect — the install is what tells us ' +
          'which store to sync.',
        showAppStore: true,
        showSupport: true,
      })
      return
    }

    // Check if user is authenticated
    if (!token) {
      // User not signed in - redirect to login with return URL. ALSO persist it via the
      // storage fallback: social sign-in (Google/Microsoft) leaves the SPA entirely
      // (window.location.href to the provider), dropping ?return_to= — their redirect pages
      // resume from urlWhenLoggedIn instead, which only ProtectedRoute used to set (and this
      // route isn't protected). Without this, a merchant choosing social login mid-install
      // stalled on /sources instead of resuming the Shopify connect.
      const resumeUrl = `/shopify-integration${location.search}`
      storage.setUrlWhenLoggedIn({ url: resumeUrl })
      // A merchant arriving from the App Store usually has no Maya account yet, so the
      // door is sign-up (with "Already have an account? Sign in", which keeps return_to).
      history.push(`/sign-up?intent=shopify&shop=${encodeURIComponent(shopDomain)}&return_to=${encodeURIComponent(resumeUrl)}`)
      return
    }

    // Wait for organizations to load
    if (loadingOrganizations) {
      setStatus('Loading organizations...')
      return
    }

    const userOrgs = getUserOrganizations()

    if (userOrgs.length === 0) {
      // No organizations found - redirect to create one. Mark the resume intent
      // explicitly: CreateOrganization only routes back here when THIS flow sent the
      // user off to create an org — never merely because 24h params exist (#1645).
      setStatus('Setting up organization...')
      markShopifyResumePending()
      history.push('/')
      return
    }

    // If multiple orgs and none selected, wait for selection
    if (userOrgs.length > 1 && !selectedOrganization) {
      return
    }

    // Use selected org or the single available org
    const currentOrganization = selectedOrganization || userOrgs[0]

    if (!currentOrganization) {
      setError({
        title: 'No organization selected',
        message: 'Please choose an organization to connect your Shopify store to, then try again.',
        canRetry: true,
      })
      return
    }

    // User is authenticated with an organization
    setStatus('Checking Shopify connection...')

    // Treat a subscription as "already connected" only when it is genuinely live AND
    // belongs to the store currently being installed. A leftover/expired subscription, or
    // one bound to a *different* store, must not short-circuit the merchant onto the
    // management page — that produced the "already connected / wrong store" review failure.
    const normalizeShop = (d?: string) => (d || '').replace('.myshopify.com', '').toLowerCase()
    const isLiveForThisStore = (sub: any): boolean =>
      !!sub
      && sub.status === 'active'
      && !sub.is_expired
      && normalizeShop(sub.shop_domain) === normalizeShop(shopDomain)

    // The single source of truth for "is THIS shop linked to this org" is the server-side
    // link check below (shop-scoped, active-only). The old client-side pre-check here was
    // ORG-scoped dead code (it read fields the /service_accounts schema doesn't expose, so
    // it always returned false) — and repairing it org-wide would have short-circuited past
    // OAuth for a NEW store while another is active, making the store-switch confirmation
    // unreachable. Removed rather than repaired.

    // Link a pending/connected account for this shop, or start OAuth
    try {
      // First, try to link any pending account from OAuth callback
      setStatus('Linking Shopify account...')
      await api.linkShopifyAccount({
        organizationId: currentOrganization.id,
        token,
        shop: shopDomain
      })
      announceServiceAccountConnected(currentOrganization.id)

      // Successfully linked - check if there's an active subscription
      try {
        const subscriptionResponse = await api.getShopifySubscription({
          organizationId: currentOrganization.id,
          token
        })

        if (isLiveForThisStore(subscriptionResponse?.subscription)) {
          // Live subscription for THIS store - redirect to subscriptions management
          setStatus('Redirecting to subscription management...')
          clearShopifyParams()
          history.push(`/organization/${currentOrganization.slug}/settings/subscriptions`)
          return
        }
      } catch (err) {
        // No subscription found, continue to billing page
      }

      // No active subscription - redirect to billing to select a plan
      setStatus('Redirecting to billing...')
      const queryString = storedParams ? buildShopifyQueryString(storedParams) : `shopify=true&shop=${shopDomain}`
      clearShopifyParams()
      history.push(`/organization/${currentOrganization.slug}/billing/products?${queryString}`)

    } catch (error: any) {
      if (error?.response?.data?.detail?.type === 'no_pending_account') {
        // No pending account - need to initiate OAuth
        setStatus('Connecting to Shopify...')
        try {
          const oauthResponse = await api.startShopifyOAuth({
            shop: shopDomain,
            organizationId: currentOrganization.id,
            token,
          })
          // Redirect to Shopify OAuth authorization page
          window.location.href = oauthResponse.authorization_url
        } catch (oauthError: any) {
          console.error('Failed to start Shopify OAuth:', oauthError)
          // Keep the stored Shopify params so "Try again" / Home can resume the flow —
          // wiping them here strands the merchant (the original onboarding regression).
          setError(toIntegrationError(oauthError, 'Couldn’t connect to Shopify'))
        }
      } else {
        console.error('Failed to link Shopify account:', error)
        // Keep the stored Shopify params so the flow can be retried/resumed.
        setError(toIntegrationError(error, 'Couldn’t connect your Shopify store'))
      }
    }
  }

  // Open the in-app HubSpot "Maya" chat (loaded via GTM on app.mayainsights.com).
  // Falls back to email if the widget isn't available (e.g. preview/staging domains).
  // The HubSpot chat (GTM-injected) isn't loaded on this bare connect screen — it lives on
  // the app shell. Flag it to open, then go to the dashboard where it loads; App opens the
  // chat once the widget is ready. clear the Shopify params so Home doesn't route back here.
  const contactSupport = () => {
    sessionStorage.setItem('open_support_chat', '1')
    clearShopifyParams()
    window.location.href = '/'
  }

  const renderOrganizationPicker = () => {
    const userOrgs = getUserOrganizations()

    return (
      <>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: '#e3f2fd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <span className="material-icons-outlined" style={{ fontSize: '32px', color: '#1976d2' }}>
            business
          </span>
        </div>
        <h2 style={{ marginTop: '0', marginBottom: '12px', color: '#333' }}>
          Select Organization
        </h2>
        <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '24px' }}>
          Choose which organization to connect with your Shopify store.
        </p>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          width: '100%'
        }}>
          {userOrgs.map((org: Organization) => (
            <button
              key={org.id}
              onClick={() => handleOrganizationSelect(org)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#1976d2'
                e.currentTarget.style.backgroundColor = '#f5f9ff'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0'
                e.currentTarget.style.backgroundColor = '#fff'
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <span className="material-icons-outlined" style={{ fontSize: '20px', color: '#1976d2' }}>
                  apartment
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600,
                  color: '#333',
                  fontSize: '16px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {org.name}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#888',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {org.slug}
                </div>
              </div>
              <span className="material-icons-outlined" style={{ color: '#999', fontSize: '20px' }}>
                chevron_right
              </span>
            </button>
          ))}
        </div>
      </>
    )
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '40px',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center'
      }}>
        {error ? (
          <>
            <div style={{ fontSize: '44px', marginBottom: '8px' }}>
              ⚠️
            </div>
            <h2 style={{ marginTop: '16px', marginBottom: '12px', color: '#1d2c38' }}>
              {error.title}
            </h2>
            <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '24px' }}>
              {error.message}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* No usable shop context: the App Store listing is the only way to get one
                  (the install hands us the shop domain + OAuth grant), so offer it first. */}
              {error.showAppStore && (
                <a
                  href={SHOPIFY_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundColor: '#739570',
                    color: 'white',
                    border: '1px solid #739570',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    textDecoration: 'none'
                  }}
                >
                  Install from the Shopify App Store
                </a>
              )}
              {error.canRetry && (
                <button
                  onClick={() => { setError(null); setStatus('Retrying…'); handleShopifyRedirect() }}
                  style={{
                    backgroundColor: '#739570',
                    color: 'white',
                    border: '1px solid #739570',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Try again
                </button>
              )}
              {error.showSupport && (
                <button
                  onClick={contactSupport}
                  style={{
                    // Secondary whenever a primary action (retry / App Store) is present,
                    // so the card never shows two green buttons competing for the click.
                    backgroundColor: error.canRetry || error.showAppStore ? '#fff' : '#739570',
                    color: error.canRetry || error.showAppStore ? '#739570' : 'white',
                    border: '1px solid #739570',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  Contact support
                </button>
              )}
              {/* Always-present escape so no error card is ever a dead end (e.g. if the
                  HubSpot chat isn't loaded). Clear the Shopify params so Home doesn't
                  immediately route back to /shopify-integration. */}
              <button
                onClick={() => { clearShopifyParams(); history.push('/') }}
                style={{
                  backgroundColor: '#fff',
                  color: '#1d2c38',
                  border: '1px solid #c7ccd1',
                  padding: '12px 24px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Back to dashboard
              </button>
            </div>
          </>
        ) : showOrgPicker && !isProcessing ? (
          renderOrganizationPicker()
        ) : (
          <>
            <ScreenLoading />
            <h2 style={{ marginTop: '24px', marginBottom: '12px', color: '#333' }}>
              Setting up Shopify Integration
            </h2>
            <p style={{ color: '#666', lineHeight: '1.6' }}>
              {status}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default ShopifyIntegration
